import { IonSearchbar, IonButton, IonSpinner, IonText } from '@ionic/react';
import { useState } from 'react';
import { SEARCH_CHANNELS } from '../../../../graphql/queries/searchChannels';
import type {
  SearchChannelsQuery,
  SearchChannelsQueryVariables,
} from '../../../../types/graphql-generated';
import {
  CHANNEL_SEARCH_PAGE_SIZE,
  CHANNEL_SEARCH_MIN_TERM_LENGTH,
} from '../../../../appConfig';
import { useInfiniteScrollQuery } from '../../../../hooks/useInfiniteScrollQuery';
import { ChannelSearchResults } from './ChannelSearchResults';

interface ChannelSearchContentProps {
  initialTerm?: string;
}

// Orchestrator for the channel search screen: owns the term state and the
// query, and picks which state to render (prompt / loading / error / empty /
// results). Search runs on an explicit "Buscar" button (not as you type); the
// offset-based infinite scroll lives in useInfiniteScrollQuery. A term arriving
// from the sub-bar (?q=) still searches automatically on mount.
export const ChannelSearchContent = ({ initialTerm = '' }: ChannelSearchContentProps) => {
  // The searchbar input.
  const [term, setTerm] = useState(initialTerm);
  // The term actually applied to the query (only changes when "Buscar" is pressed
  // or a term arrives from the sub-bar).
  const [activeTerm, setActiveTerm] = useState(initialTerm.trim());

  const hasActiveSearch = activeTerm.length >= CHANNEL_SEARCH_MIN_TERM_LENGTH;
  const canSearch = term.trim().length >= CHANNEL_SEARCH_MIN_TERM_LENGTH;

  const { data, loading, error, loadMore, resetPagination } = useInfiniteScrollQuery<
    SearchChannelsQuery,
    SearchChannelsQueryVariables
  >({
    query: SEARCH_CHANNELS,
    buildVariables: (offset) => ({ term: activeTerm, limit: CHANNEL_SEARCH_PAGE_SIZE, offset }),
    pageSize: CHANNEL_SEARCH_PAGE_SIZE,
    skip: activeTerm.length < CHANNEL_SEARCH_MIN_TERM_LENGTH,
    append: (previous, next) => ({
      searchChannels: {
        ...next.searchChannels,
        channels: [...previous.searchChannels.channels, ...next.searchChannels.channels],
      },
    }),
  });

  const channels = data?.searchChannels.channels ?? [];
  const totalCount = data?.searchChannels.totalCount ?? 0;
  const hasMore = channels.length < totalCount;

  const handleInput = (value: string) => {
    setTerm(value);
  };

  const handleSearch = () => {
    const trimmed = term.trim();
    if (trimmed.length >= CHANNEL_SEARCH_MIN_TERM_LENGTH) {
      setActiveTerm(trimmed);
      resetPagination();
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <IonSearchbar
          value={term}
          onIonInput={(e) => handleInput(e.detail.value ?? '')}
          onIonClear={() => handleInput('')}
          placeholder="Buscar canal..."
          debounce={0}
        />

        {term.length > 0 && term.trim().length < CHANNEL_SEARCH_MIN_TERM_LENGTH && (
          <IonText color="medium">
            <p className="text-sm px-4">
              Escribe al menos {CHANNEL_SEARCH_MIN_TERM_LENGTH} caracteres para buscar
            </p>
          </IonText>
        )}

        <IonButton expand="block" onClick={handleSearch} disabled={!canSearch || loading}>
          {loading ? 'Buscando...' : 'Buscar'}
        </IonButton>
      </div>

      {/* Prompt: nothing searched yet */}
      {!hasActiveSearch && (
        <div className="flex flex-col justify-center items-center min-h-[200px] text-center px-4">
          <IonText color="medium">
            <p className="text-lg">Escribe el nombre de un canal para buscarlo</p>
          </IonText>
        </div>
      )}

      {/* Loading */}
      {hasActiveSearch && loading && channels.length === 0 && (
        <div className="flex justify-center items-center min-h-[200px]">
          <IonSpinner name="crescent" />
        </div>
      )}

      {/* Error */}
      {hasActiveSearch && error && (
        <IonText color="danger">
          <p>Error al buscar canales: {error.message}</p>
        </IonText>
      )}

      {/* Empty */}
      {hasActiveSearch && !loading && !error && channels.length === 0 && (
        <div className="flex justify-center items-center min-h-[200px]">
          <IonText color="medium">
            <p>No se encontraron canales con &quot;{activeTerm}&quot;</p>
          </IonText>
        </div>
      )}

      {/* Results */}
      {hasActiveSearch && channels.length > 0 && (
        <ChannelSearchResults channels={channels} hasMore={hasMore} onLoadMore={loadMore} />
      )}
    </div>
  );
};
